import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AuthLayout from '../components/auth/AuthLayout';
import EmailIcon from '@mui/icons-material/Email';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';

const ForgotPassword = () => {
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Enter a valid email')
      .required('Email is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        setError('');
        setSuccess('');

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));

        // TODO: Replace with actual password reset logic
        console.log('Password reset requested for:', values.email);

        // Show success message
        setSuccess('Password reset link has been sent to your email.');
      } catch (err) {
        setError('Failed to send reset link. Please try again.');
        console.error('Forgot password error:', err);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout
      title="Forgot Password"
      icon={EmailIcon}
    >
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Enter your email address and we'll send you a link to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
          {error}
        </Alert>
      )}

      {success ? (
        <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
          {success}
        </Alert>
      ) : (
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </form>
      )}

      <Grid container justifyContent="center" sx={{ mt: 2 }}>
        <Grid item>
          <Link
            component={RouterLink}
            to="/login"
            variant="body2"
            sx={{
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Back to Sign In
          </Link>
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

export default ForgotPassword;
